import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { ShoppingBag, Menu, X, Search, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useProductSearch } from '../hooks/useProductSearch';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import CartPanel from './CartPanel';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { count, isOpen, setIsOpen } = useCart();
  const { user, profile, setLoginModalOpen } = useAuth();

  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [searchSession, setSearchSession] = useState(0);
  const { results, loading: searchLoading } = useProductSearch(searchQuery, { sessionKey: searchSession });
  const [showSearchOverlay, setShowSearchOverlay] = useState(false);
  const inputRef = React.useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { to: '/', label: 'Home' },
    { to: '/shop', label: 'Shop' },
    { to: '/ingredients', label: 'Ingredients' },
    { to: '/about', label: 'About Us' },
    { to: '/contact', label: 'Contact Us' },
    { to: '/blog', label: 'Blog' },
    { to: '/track-order', label: 'Track Order' },
  ];

  return (
    <>
      {/* Announcement bar */}
      <div className="bg-black text-white text-center py-2 text-xs tracking-widest uppercase font-light">
        Free shipping above ₹499 &nbsp;·&nbsp; Dermatologically Tested &nbsp;·&nbsp; No Secrets, Only Science
      </div>

      <nav className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur shadow-sm' : 'bg-white'} border-b border-gray-100`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 -ml-4">
              <img
                src="/logo.png"
                alt="Metameni"
                className="h-8 w-auto object-contain"
              />
            </Link>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-5 lg:gap-8">
              {links.map(l => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === '/'}
                  className={({ isActive }) =>
                    `text-xs tracking-[0.15em] uppercase font-medium transition-colors duration-200 ${isActive ? 'text-black border-b border-black pb-0.5' : 'text-gray-500 hover:text-black'
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </div>

            {/* Search + Actions */}
            <div className="flex items-center gap-4">
              {/* Search icon opens floating overlay */}
              <button
                onClick={() => { setSearchQuery(''); setSearchSession(s => s + 1); setShowSearchOverlay(true); setTimeout(() => inputRef.current?.focus(), 50); }}
                className="text-gray-600 hover:text-black transition-colors hidden md:block"
                aria-label="Open search"
              >
                <Search size={18} />
              </button>

              {/* Actions */}
              <button
                type="button"
                onClick={() => (user ? navigate('/profile') : setLoginModalOpen(true))}
                className="text-gray-600 hover:text-black transition-colors relative"
                aria-label={user ? 'My profile' : 'Sign in'}
                title={user ? (profile?.full_name || user.phoneNumber || 'Profile') : 'Sign in'}
              >
                <User size={20} />
                {user && (
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full border border-white" />
                )}
              </button>
              <button
                onClick={() => setIsOpen(true)}
                className="relative text-gray-600 hover:text-black transition-colors"
              >
                <ShoppingBag size={22} />
                <span className="absolute -top-1.5 -right-1.5 bg-black text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-medium">
                  {count}
                </span>
              </button>
              <button
                className="md:hidden text-gray-600 hover:text-black"
                onClick={() => setMenuOpen(!menuOpen)}
              >
                {menuOpen ? <X size={22} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Floating search overlay */}
        {showSearchOverlay && (
          <div className="fixed inset-0 z-60 flex items-start justify-center pt-24">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowSearchOverlay(false)} />
            <div className="relative w-full max-w-2xl px-4">
              <div className="bg-white rounded-md shadow-lg border border-gray-100">
                <div className="p-3">
                  <div className="relative">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      ref={inputRef}
                      aria-label="Search products"
                      type="search"
                      placeholder="Search products"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          setShowSearchOverlay(false);
                          navigate(`/shop?search=${encodeURIComponent(searchQuery)}`);
                          setSearchQuery('');
                        }
                      }}
                      className="pl-9 pr-4 py-3 border border-gray-200 rounded-md w-full text-sm"
                    />
                  </div>
                </div>

                {/* Suggestions */}
                {results.length > 0 ? (
                  <div className="max-h-80 overflow-auto border-t border-gray-100">
                    {results.map(r => (
                      <button
                        key={r.id}
                        onMouseDown={(e) => { e.preventDefault(); setShowSearchOverlay(false); navigate(`/product/${r.id}`); setSearchQuery(''); }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3"
                      >
                        <img src={r.image || '/logo.jpeg'} alt="" className="w-12 h-12 object-cover rounded-sm" />
                        <div>
                          <div className="text-sm font-medium text-gray-800">{r.name}</div>
                          {r.shortName && <div className="text-xs text-gray-500">{r.shortName}</div>}
                        </div>
                        <div className="ml-auto text-sm font-medium">₹{r.price.toLocaleString()}</div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-6 text-center text-sm text-gray-500">{searchQuery ? 'No matches yet' : 'Start typing to search products'}</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-6 py-6 flex flex-col gap-5">
            {links.map(l => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === '/'}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `text-sm tracking-widest uppercase font-medium ${isActive ? 'text-black' : 'text-gray-500'}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        )}
      </nav>

      <CartPanel />
    </>
  );
}
